import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import bgImage from 'src/assets/images/dashboard_image.png'
import axios from 'src/axios/axios'
import { Checkbox } from 'src/components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/components/ui/table'
import { TooltipProvider } from 'src/components/ui/tooltip'
import ApproveBulk from 'src/components/workflow/bulk/ApproveBulk'
import RejectBulk from 'src/components/workflow/bulk/RejectBulk'
import { useAppSelector } from 'src/hooks/redux'
import useAuth from 'src/hooks/useAuth'
import { RootState } from 'src/store'
import lodash from 'lodash'
import { getWindevLang } from 'src/lib/lang'
import { useTranslation } from 'react-i18next'
import { format, isValid } from 'date-fns'
import { YYYYMMDDtoDate } from 'src/functions/format'
import Preview from 'src/components/files/Preview'
import { Sheet, SheetContent, SheetTrigger } from 'src/components/ui/sheet'
import { EyeIcon } from 'lucide-react'
import FilePreview from 'src/components/files/FilePreview'
import { ScrollArea, ScrollBar } from 'src/components/ui/scroll-area'


const PreviewDocumentTable = ({docid, filename}:{docid:number, filename:string}) => {
    const [open, setopen] = useState(false);

    useEffect(()=>{
        if(!open){ return }
    }, [open])

    return (
        <Sheet open={open} onOpenChange={(o)=>{setopen(o)}}>
            <SheetTrigger asChild><button className='flex items-center justify-center w-8 h-8 rounded-full min-w-8 min-h-8 hover:bg-slate-200'><EyeIcon className='w-4 h-4 text-slate-600'/></button></SheetTrigger>
            <SheetContent position='bottom' className='max-h-[90dvh] min-h-[400px] h-full'>
                <div className='flex w-full h-full overflow-auto'>
                    <ScrollArea className='flex w-full'>
                        <div className='relative w-full h-full'>
                        <Preview docid={docid} filename={filename} />
                        </div>
                        <ScrollBar orientation="horizontal"/>
                    </ScrollArea>
                </div>
            </SheetContent>
        </Sheet>
    )
}


const DataRow = ({document, data, getColumns, selected_docids, toggleSelect, doctypes}:{document:any, data:any, getColumns:any, selected_docids:any, toggleSelect:any, doctypes:any}) => {
    const { i18n } = useTranslation()
    const { auth } = useAuth()

    return (
        <TableRow key={`row-${document.docid}`} className={`${!document?.options?.wf && 'opacity-50'}`}>
            <TableCell>{document?.options?.wf ? <div className='flex items-center'><Checkbox onCheckedChange={(c)=>{toggleSelect(document?.docid)}} checked={selected_docids?.includes(document.docid) || false}/></div> : <></>}</TableCell>
            <TableCell>{document?.docid}</TableCell>
            <TableCell>{Array.isArray(doctypes) ? (doctypes.find((i:any)=>i.doctype===document.doctype)?.docoms.find((i:any)=>i.nationid===getWindevLang(i18n.language))?.oms) : document.doctype}</TableCell>
            <TableCell>{document?.docdate ? (isValid(YYYYMMDDtoDate(document.docdate)) ? format(YYYYMMDDtoDate(document.docdate), "dd/MM/yyyy") : '') : ''}</TableCell>
            <TableCell>{document?.filename}</TableCell>
            {data && data && Array.isArray(getColumns(data)) && getColumns(data).map((v:any, i:any)=>(
                <TableCell key={i}>{document?.metadata.find((md:any) => md.metatype === v)?.metavalue || ''}</TableCell>
            ))}
            <TableCell><div className='flex items-center justify-end'><PreviewDocumentTable filename={document?.filename || ''} docid={document?.docid || 0}/></div></TableCell>
        </TableRow>
    )
}

const Home = () => {
    const { t, i18n } = useTranslation()
    const { auth } = useAuth()

    const metatypes:MetatypeObject[] = useAppSelector((state:RootState)=>state.metatypes)
    const doctypes:DoctypeObject[] = useAppSelector((state:RootState)=>state.doctypes)

    const { data, isLoading } = useQuery<any[] | null>({
        queryKey: [`workflow-list`],
        queryFn: async () => {
            try {
                const { data } = await axios.get(`wf/active/list`)
                if(data.errorcode!==0){ throw new Error(data.message) }
                if(!Array.isArray(data.object)){ throw new Error('No open workflows') }
                return data.object
            } catch (error) {
                console.log(error);
                return null
            }
        }
    })

    const { data:allVerificationSettings } = useQuery({
        queryKey: [`all-verification-settings`],
        queryFn: async () => {
            try {
                const arrWfActionId = lodash.uniqWith(data?.map((d:any)=>d?.wf?.wfdef_actionid).filter((i:any)=>typeof(i)==='number'), lodash.isEqual)
                
                const promises = arrWfActionId.map(actionid => 
                    axios.get(`wf/verificationsettings?wfdef_actionid=${actionid}`)
                    .then(({data}) => {
                        if(data.errorcode!==0){throw new Error(`getting wf vs ${actionid} failed`)}
                        return {actionid:actionid, ...data.object}
                    })
                );

                const results = await Promise.all(promises);

                return results

            } catch (error) {
                return null
            }
        },
        refetchOnWindowFocus: false,
        enabled: !!(data)
    })

    const [docid_added_wf, setdocid_added_wf] = useState<number[]>([]);
    const [selected_docids, setselected_docids] = useState<number[]>([]);

    function handleOnWorkflowDone(docids:number[]) {
        setdocid_added_wf([...docid_added_wf.concat(docids)])
    }

    const getColumns = (results:any[]) => {
        try { 
            if(!results){ return []; }
            if(!metatypes){ return []; }
            let result:any[] = []
            let metatypeArray:string[] = []
            results.forEach(element => {
                return element.metadata.forEach((md:any) => {
                    if(!metatypeArray.includes(md.metatype)){ metatypeArray.push(md.metatype) }
                })
            });
            result = metatypeArray.filter(mt => {
                const item = metatypes.find((i:any)=>i.metatype===mt)
                return item?.columndisplay
            })
            return result
        } catch (e) { return [] }
    }

    function toggleSelect(id:number) {
        setselected_docids(p=>{
            let u = [...p]
            const i = u.indexOf(id)
            if(i<0){ u.push(id) }
            else{ u.splice(i, 1) }
            return u
        })
    }
    
    function toggleSelectAll() {
        setselected_docids(p=>{
            let u = [...p]
            if(p.length > 0){ u = [] }
            else {
                if(Array.isArray(data)){
                    const d = data.filter((d:any)=>d.options?.wf).map((d:any)=>d.docid)
                    u = d
                }
            }
            return u
        })
    }

    const allEqual = (arr:any[]) => arr.every( v => v === arr[0] )

    function allSelectedSameVerificationSettings() {
        let isSame = false;
        if(!Array.isArray(allVerificationSettings)){ return isSame; }
        if(!data){ return isSame; }
        if(!Array.isArray(data)){ return isSame; }

        const selectedActionIds:number[] = []
        selected_docids.forEach(docid => {
            const wfid = data?.find((d:any)=>d.docid===docid)?.wf?.wfdef_actionid || 0
            selectedActionIds.push(wfid)
        });
        
        isSame = allEqual(selectedActionIds)

        return isSame
    }

    const actionid = data?.find((d:any)=>d.docid === selected_docids[0])?.wf?.wfdef_actionid || 0

    let filteredDocuments = (data && Array.isArray(data)) ? [...data] : []
    filteredDocuments = filteredDocuments.filter(d=>!docid_added_wf.includes(d.docid))

  return (
    <>
    <div className='flex flex-col overflow-auto h-dvh'>
        <div className='relative flex flex-col gap-2 p-4 md:p-16 bg-primary'>
            <h1 className='relative z-10 text-3xl font-bold text-white'>Workflows</h1>
            <p className='relative z-10 text-sm text-white uppercase'>Beheer hier uw openstaande workflows</p>
            <img src={bgImage} className='absolute top-0 left-0 object-cover w-full h-full' alt="bg" />
        </div>
        <div className='flex flex-col flex-1 gap-4 px-4 py-4 overflow-auto md:px-16 md:py-8'>

            {(docid_added_wf.length === data?.length) && 
            <div className='flex flex-col items-center justify-center w-full gap-10'>
                <p className='text-4xl font-bold lg:text-7xl'>{t('Bedankt')}.</p>
                <p>{t('Alle documenten zijn behandeld')}</p>
            </div>
            }

            {!(docid_added_wf.length === (data && data?.length)) &&
            <>
            <div className='sticky flex items-center justify-between w-full p-1 bg-white border rounded-md shadow-sm -top-8 min-h-10'>
                <TooltipProvider delayDuration={0}>
                <div className='flex items-center gap-1'>
                    <ApproveBulk onSuccess={()=>{handleOnWorkflowDone([...selected_docids]); setselected_docids([]); }} metatypes={metatypes} disabled={!allSelectedSameVerificationSettings()} docids={selected_docids} verification_settings={allVerificationSettings?.find(s=>s.actionid===actionid) || undefined}/>
                    <RejectBulk onSuccess={()=>{handleOnWorkflowDone([...selected_docids]); setselected_docids([]); }} metatypes={metatypes} disabled={!allSelectedSameVerificationSettings()} docids={selected_docids} verification_settings={allVerificationSettings?.find(s=>s.actionid===actionid) || undefined}/>
                </div>
                <div>
                    {/* <DownloadBulk docids={selected_docids} /> */}
                </div>
                </TooltipProvider>
            </div>

            <div className='overflow-auto bg-white border rounded-md'>
                <Table>
                    <TableHeader className='bg-slate-100'><TableRow>
                        <TableHead className='whitespace-nowrap'><div className='flex items-center'><Checkbox checked={selected_docids?.length > 0 || false} onCheckedChange={()=>{toggleSelectAll()}}/></div></TableHead>
                        <TableHead className='whitespace-nowrap'>Document ID</TableHead>
                        <TableHead className='whitespace-nowrap'>Document type</TableHead>
                        <TableHead className='whitespace-nowrap'>Document datum</TableHead>
                        <TableHead className='whitespace-nowrap'>Bestandsnaam</TableHead>
                        {filteredDocuments && Array.isArray(getColumns(filteredDocuments)) && getColumns(filteredDocuments).map((v, i)=>(
                            <TableHead key={i}>{Array.isArray(metatypes) ? metatypes.find((i:any)=>i.metatype===v)?.metatype_oms?.find((i:any)=>i.nationid===getWindevLang(i18n.language))?.oms || v : ''}</TableHead>
                        ))}
                        <TableHead></TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {(filteredDocuments && filteredDocuments.length === 0 && !isLoading) && <TableRow><TableCell colSpan={99}>Geen openstaande workflows</TableCell></TableRow>}
                        {(filteredDocuments && filteredDocuments.length === 0 && isLoading) && <TableRow><TableCell colSpan={99}>Even geduld...</TableCell></TableRow>}
                        {(filteredDocuments && Array.isArray(filteredDocuments)) && filteredDocuments.map((document:any)=>(
                            <DataRow doctypes={doctypes} selected_docids={selected_docids} toggleSelect={toggleSelect} data={filteredDocuments} document={document} getColumns={getColumns} key={`row-${document.docid}`}/>
                        ))}
                    </TableBody>
                </Table>
            </div>
            </>
            }
        </div>
    </div>
    </>
  )
}

export default Home